import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { consts } from '../../../consts';
import { Utils } from '../../../shared/utils';
import { FinanciacionPrivadaModel, DeudaACortoPlazoFinanciacionPrivadaModel } from './financiacion-privada.model';
import { FinanciacionPrivadaService } from './financiacion-privada.service';
import { ActivityService } from '../../user/activity/activity.service';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../modules_v2/shared/components/confirm-dialog.component';
import { stripFinancingConcept } from '../../../modules_v2/finance/financing-detail/financing-detail.model';
import {
  currentActivityOption,
  EntityFormOption,
  fromApiDate,
  InjectedOptions,
  optionId,
} from '../../../modules_v2/finance/entity-form/entity-form.utils';

@Component({
    selector: 'convfact-financiacionprivadadeudaacortoplazo',
    templateUrl: './financiacion-privada-dialog-deuda-a-corto-plazo.component.html',
    styleUrls: ['./financiacion-dialogs.component.css'],
    standalone: false
})
export class FinanciacionPrivadaDialogDeudaACortoPlazoComponent implements OnInit {

  public financiacionPrivada: FinanciacionPrivadaModel;
  public editing: DeudaACortoPlazoFinanciacionPrivadaModel | null = null;

  public dataSource: DeudaACortoPlazoFinanciacionPrivadaModel[] = [];
  public activities: EntityFormOption[] = [];

  public ready = false;
  public loadError = false;
  public saving = false;
  public savingMessage = '';
  public submitted = false;
  public saveError = '';

  public form: UntypedFormGroup;

  private model: DeudaACortoPlazoFinanciacionPrivadaModel = new DeudaACortoPlazoFinanciacionPrivadaModel();
  private changed = false;
  private requestedEditDone = false;
  private readonly injected = new InjectedOptions();

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<FinanciacionPrivadaDialogDeudaACortoPlazoComponent, boolean>,
    public financiacionPrivadaService: FinanciacionPrivadaService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public activityService: ActivityService,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.financiacionPrivada = data.financiacionPrivada;
    this.form = this.fb.group({
      fecha: [null],
      importe: [null],
      concepto: [''],
      activity: [null],
    });
  }

  get totalReclassified(): number {
    return Utils.RoundTwoDecimals(this.dataSource.reduce((acc, item) => acc + (Number(item.importe) || 0), 0));
  }

  get conceptoLength(): number {
    return String(this.form.get('concepto').value || '').length;
  }

  get missingDate(): boolean { return !this.form.get('fecha').value; }
  get missingConcept(): boolean { return !String(this.form.get('concepto').value || '').trim(); }
  get invalidImporte(): boolean { return !Number(this.form.get('importe').value); }
  get missingActivity(): boolean { return optionId(this.form.get('activity').value) === null; }

  public control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  public ngOnInit(): void {
    this.load();
  }

  public retry(): void {
    this.loadError = false;
    this.load();
  }

  public startEdit(item: DeudaACortoPlazoFinanciacionPrivadaModel): void {
    if (this.saving) return;
    this.editing = item;
    this.submitted = false;
    this.saveError = '';
    this.injected.clear();

    this.model = { ...item } as DeudaACortoPlazoFinanciacionPrivadaModel;

    this.form.patchValue({
      fecha: fromApiDate(item.fecha),
      importe: item.importe != null ? Number(item.importe) : null,
      concepto: this.itemConcept(item),
      activity: this.injected.ensure(this.activities, item.activitiesUid, item.activitiesName),
    }, { emitEvent: false });
  }

  public cancelEdit(): void {
    this.editing = null;
    this.resetForm();
  }

  public pay(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    if (this.missingDate || this.missingConcept || this.invalidImporte || this.missingActivity) {
      return;
    }

    const isEdit = !!this.editing;
    const payload = this.buildPayload();

    const task = isEdit
      ? this.financiacionPrivadaService.deudaACortoPlazoUpdate(payload)
      : this.financiacionPrivadaService.deudaACortoPlazoCreate(payload);

    this.setSaving(true, isEdit ? 'Actualizando deuda a corto plazo…' : 'Registrando deuda a corto plazo…');
    task
      .then(() => {
        this.changed = true;
        this.setSaving(false);
        this.notify.ok(isEdit ? 'Deuda a corto plazo actualizada correctamente' : 'Deuda a corto plazo añadida correctamente');
        if (!isEdit || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.editing = null;
        this.resetForm();
        this.reloadList();
      })
      .catch(error => {
        this.setSaving(false);
        this.saveError = this.serverMessage(error) || (isEdit
          ? 'Ha ocurrido un error inesperado al actualizar el elemento'
          : 'Ha ocurrido un error inesperado al añadir el elemento');
      });
  }

  public delete(item: DeudaACortoPlazoFinanciacionPrivadaModel): void {
    if (this.saving) return;
    this.confirm.open({
      title: 'Eliminar deuda a corto plazo',
      subtitle: this.financiacionPrivada.descripcion || undefined,
      message: 'Se eliminará la reclasificación a corto plazo y su asiento contable. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.itemDetails(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando deuda a corto plazo…',
      errorText: 'Ha ocurrido un error al eliminar el elemento',
      action: () => this.financiacionPrivadaService.deudaACortoPlazoDelete(item.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      this.notify.ok('Deuda a corto plazo eliminada correctamente');
      if (this.editing && this.editing.uid === item.uid) this.cancelEdit();
      this.reloadList();
    });
  }

  public itemConcept(item: { concepto: string }): string {
    return stripFinancingConcept(item.concepto, this.financiacionPrivada.descripcion);
  }

  public closeDialog(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  public trackByUid(_: number, item: DeudaACortoPlazoFinanciacionPrivadaModel): string {
    return item.uid;
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const item = this.dataSource.find(entry => entry.uid === this.data.editUid);
    if (item) this.startEdit(item);
  }

  private load(): void {
    this.ready = false;
    Promise.all([
      this.loadActivities(),
      this.financiacionPrivadaService.deudaACortoPlazoList(this.financiacionPrivada.uid),
    ])
      .then(([activities, list]) => {
        this.activities = activities;
        this.dataSource = list || [];
        this.resetForm();
        this.openRequestedEdit();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private reloadList(): void {
    this.financiacionPrivadaService.deudaACortoPlazoList(this.financiacionPrivada.uid)
      .then(list => { this.dataSource = list || []; })
      .catch(() => this.notify.error('No se pudo recargar el listado de deuda a corto plazo'));
  }

  private loadActivities(): Promise<EntityFormOption[]> {
    return this.activityService.getActiveList()
      .then(list => (list || [])
        .filter(activity => activity && activity.uid && activity.uid !== consts.EMPTY_GUID)
        .map(activity => ({ id: activity.uid, display: activity.name || '' })))
      .catch(() => [] as EntityFormOption[]);
  }

  private resetForm(): void {
    this.injected.clear();
    this.submitted = false;
    this.saveError = '';
    this.model = new DeudaACortoPlazoFinanciacionPrivadaModel();
    this.form.reset({
      fecha: null,
      importe: null,
      concepto: '',
      activity: null,
    }, { emitEvent: false });
    this.applySingleOptionDefaults();
  }

  private applySingleOptionDefaults(): void {
    if (this.editing) return;
    if (!this.missingActivity) return;

    const current = currentActivityOption(this.activities);
    if (current) this.control('activity').setValue(current, { emitEvent: false });
    else if (this.activities.length === 1) this.control('activity').setValue(this.activities[0], { emitEvent: false });
  }

  private buildPayload(): DeudaACortoPlazoFinanciacionPrivadaModel {
    const value = this.form.getRawValue();
    const payload = { ...this.model } as DeudaACortoPlazoFinanciacionPrivadaModel;
    payload.financiacionPrivadaId = this.financiacionPrivada.uid;
    payload.fecha = value.fecha;
    payload.importe = Number(value.importe) || 0;
    payload.concepto = String(value.concepto || '').trim();
    payload.activitiesUid = optionId(value.activity);
    return payload;
  }

  private setSaving(saving: boolean, message = ''): void {
    this.saving = saving;
    this.savingMessage = message;
    this.dialogRef.disableClose = saving;
  }

  private itemDetails(item: DeudaACortoPlazoFinanciacionPrivadaModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [
      { label: 'Importe', value: Utils.FormatMoney(Number(item.importe) || 0) + ' €' },
    ];
    if (item.fecha) details.push({ label: 'Fecha', value: formatDate(item.fecha, 'dd/MM/yyyy', 'es-ES') });
    if (item.concepto) details.push({ label: 'Concepto', value: this.itemConcept(item) });
    return details;
  }

  private serverMessage(error: any): string {
    return (error && error.error && error.error.message) || '';
  }
}
